import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem,
  Stack, Switch, TextField, Typography
} from '@mui/material';
import { apiGet } from 'api/service';
import { createWebAuthProfile, deleteWebAuthProfile, listWebAuthProfiles, testWebAuthProfileConnection, updateWebAuthProfile } from 'api/webAuthProfiles';
import { GREEN, MONO } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const TYPES = { bearer: 'Access token', basic: 'Username and password', headers: 'Custom headers', cookies: 'Cookies' };
const BLANK = { name: '', target: '', auth_type: 'bearer', username: '', password: '', token: '', headers_text: '{\n  "Authorization": "Bearer <token>"\n}', cookies_text: '{\n  "session": "<value>"\n}', notes: '', is_default: false };

const parseObject = (label, raw) => {
  const text = (raw || '').trim();
  if (!text) return {};
  let parsed;
  try { parsed = JSON.parse(text); } catch { throw new Error(`${label} must be valid JSON, like {"name": "value"}.`); }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error(`${label} must be a JSON object, like {"name": "value"}.`);
  return parsed;
};
const fail = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

export default function WebAuthProfilesPage() {
  const [rows, setRows] = useState(null);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [tests, setTests] = useState({});
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([listWebAuthProfiles(), apiGet('/api/user-targets/')]);
      const profiles = p?.results || p || [];
      const own = t?.results || t || [];
      setRows(Array.isArray(profiles) ? profiles : []);
      setSites((Array.isArray(own) ? own : []).filter((ut) => ['domain', 'url', 'ip'].includes((ut?.target?.type || '').toLowerCase())));
    } catch (e) { setError(fail(e, 'Could not load your saved logins.')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const change = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const create = async () => {
    setSaving(true); setFormError('');
    try {
      const body = { name: form.name.trim(), target: Number(form.target), auth_type: form.auth_type, username: '', password: '', token: '', headers: {}, cookies: {}, notes: form.notes, enabled: true, is_default: form.is_default };
      if (form.auth_type === 'basic') { body.username = form.username; body.password = form.password; }
      if (form.auth_type === 'bearer') body.token = form.token;
      if (form.auth_type === 'headers') body.headers = parseObject('Headers', form.headers_text);
      if (form.auth_type === 'cookies') body.cookies = parseObject('Cookies', form.cookies_text);
      await createWebAuthProfile(body);
      setOpen(false); setForm(BLANK); await load();
    } catch (e) { setFormError(e?.response?.data?.name?.[0] || fail(e, 'Could not save it.')); } finally { setSaving(false); }
  };
  const act = async (fn, failText) => { setError(''); try { await fn(); await load(); } catch (e) { setError(fail(e, failText)); } };
  const test = async (row) => {
    setTests((t) => ({ ...t, [row.id]: { wait: true } }));
    try {
      const r = await testWebAuthProfileConnection(row.id, {});
      setTests((t) => ({ ...t, [row.id]: { ok: Boolean(r?.ok), message: r?.message || 'Done.' } }));
    } catch (e) { setTests((t) => ({ ...t, [row.id]: { ok: false, message: fail(e, 'The test failed.') } })); }
  };

  if (rows === null) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  const siteName = (id) => { const s = sites.find((ut) => ut.target?.id === id); return s?.target?.value || ''; };
  const ready = form.name.trim() && form.target;

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Saved logins</Typography>
          <Typography color="text.secondary" variant="body2">Let scans sign in to your website, so they can test the pages behind the login</Typography>
        </Box>
        <Button variant="contained" onClick={() => { setForm(BLANK); setFormError(''); setOpen(true); }} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: GREEN } }}>Add a login</Button>
      </Stack>
      <Alert severity="info">Passwords, tokens and cookie values are stored but never shown again. Use an account made for testing, not an administrator.</Alert>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        {!rows.length && <Typography color="text.secondary" sx={{ p: 3 }}>No saved logins yet.</Typography>}
        {rows.map((r, i) => {
          const t = tests[r.id];
          return (
            <Stack key={r.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.6, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
              <Box sx={{ flex: '2 1 240px', minWidth: 0 }}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
                  {r.is_default && <Pill color={GREEN}>USED BY DEFAULT</Pill>}
                  {!r.enabled && <Pill color="#94a3b8">PAUSED</Pill>}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{r.target_value || siteName(r.target) || 'No website'} · {TYPES[r.auth_type] || r.auth_type}{r.masked_secret ? ` · ${r.masked_secret}` : ''}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                {t?.wait && <Typography variant="body2" color="text.secondary">Testing…</Typography>}
                {t && !t.wait && <Typography variant="body2" sx={{ color: t.ok ? GREEN : '#f87171' }}>{t.ok ? 'Works' : 'Does not work'}: {t.message}</Typography>}
              </Box>
              <Button size="small" onClick={() => test(r)} disabled={!r.target} sx={{ textTransform: 'none', color: GREEN, fontWeight: 700 }}>Test</Button>
              {!r.is_default && <Button size="small" color="inherit" onClick={() => act(() => updateWebAuthProfile(r.id, { is_default: true, enabled: true }), 'Could not set it as the default.')} sx={{ textTransform: 'none' }}>Use by default</Button>}
              <Button size="small" color="inherit" onClick={() => act(() => updateWebAuthProfile(r.id, { enabled: !r.enabled }), 'Could not change it.')} sx={{ textTransform: 'none' }}>{r.enabled ? 'Pause' : 'Resume'}</Button>
              <Button size="small" color="error" onClick={() => act(() => deleteWebAuthProfile(r.id), 'Could not delete it.')} sx={{ textTransform: 'none' }}>Delete</Button>
            </Stack>
          );
        })}
      </Box>

      <Dialog open={open} onClose={() => (saving ? null : setOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Add a login</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField size="small" label="Name" placeholder="Test customer account" value={form.name} onChange={change('name')} />
            <TextField select size="small" label="Website" value={form.target} onChange={change('target')} helperText={sites.length ? 'Scans only use this login on this website' : 'Add a website first'}>
              {sites.map((ut) => <MenuItem key={ut.id} value={ut.target.id}>{ut.target.value}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="How it signs in" value={form.auth_type} onChange={change('auth_type')}>
              {Object.entries(TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            {form.auth_type === 'basic' && <Stack direction="row" gap={1.5}><TextField fullWidth size="small" label="Username" value={form.username} onChange={change('username')} /><TextField fullWidth size="small" type="password" label="Password" autoComplete="new-password" value={form.password} onChange={change('password')} /></Stack>}
            {form.auth_type === 'bearer' && <TextField size="small" type="password" label="Access token" autoComplete="new-password" value={form.token} onChange={change('token')} />}
            {form.auth_type === 'headers' && <TextField multiline minRows={4} size="small" label="Headers (JSON)" value={form.headers_text} onChange={change('headers_text')} InputProps={{ sx: { fontFamily: MONO, fontSize: 13 } }} />}
            {form.auth_type === 'cookies' && <TextField multiline minRows={4} size="small" label="Cookies (JSON)" value={form.cookies_text} onChange={change('cookies_text')} InputProps={{ sx: { fontFamily: MONO, fontSize: 13 } }} />}
            <TextField size="small" label="Notes" value={form.notes} onChange={change('notes')} />
            <FormControlLabel control={<Switch checked={form.is_default} onChange={(e) => setForm((p) => ({ ...p, is_default: e.target.checked }))} />} label="Use this login by default" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={saving || !ready} variant="contained" onClick={create} sx={{ bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: GREEN } }}>{saving ? 'Saving…' : 'Save login'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
